import { useState } from 'react';
import { Info } from 'lucide-react';
import { Accordion } from 'components/Accordion';
import { Tooltip } from 'common/components';
import { CallStatistic } from 'types/call.type';
import styles from './Statistic.module.scss';

const EMPTY_CALL_STATS: CallStatistic = {
    pending: 0,
    overDue: 0,
    dialing: 0,
    ringing: 0,
    inProgress: 0,
    failed: 0,
    busy: 0,
    noAnswer: 0,
    voicemail: 0,
    voicemailSuccess: 0,
    voicemailFailed: 0,
    userAbandoned: 0,
    agentAbandoned: 0,
    transferCompleted: 0,
    transferFailed: 0,
    transferInProgress: 0,
    transferUserAbandoned: 0,
    noAgentsAvailable: 0,
    agentRejected: 0,
    humanOnCall: 0,
    total: 0,
    transfer: 0,
    badNumber: 0,
    blocked: 0,
    completed: 0,
    contacted: 0,
};

export const Statistic = ({ callStats }: { callStats?: CallStatistic }) => {
    const [isAccordionOpen, setIsAccordionOpen] = useState(true);

    const {
        overDue,
        pending,
        ringing,
        inProgress,
        failed,
        busy,
        noAnswer,
        voicemail,
        voicemailSuccess,
        voicemailFailed,
        userAbandoned,
        agentAbandoned,
        transferCompleted,
        transferFailed,
        transferInProgress,
        transferUserAbandoned,
        noAgentsAvailable,
        agentRejected,
        humanOnCall,
        total,
        transfer,
        badNumber,
        blocked,
        completed,
        contacted,
    } = { ...EMPTY_CALL_STATS, ...callStats };

    return (
        <div className={styles.callStatistics}>
            <Accordion
                isOpen={isAccordionOpen}
                onToggle={() => setIsAccordionOpen(!isAccordionOpen)}
                header={
                    <div className={styles.totalCallsLabel}>
                        <span>Call status overview:</span>
                        <span> {total}</span>
                    </div>
                }
                className={styles.accordion}
            >
                <div className={styles.statsCardsGrid}>
                    <div className={`${styles.statCard} ${styles.pendingCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Queued/Overdue
                                <Tooltip
                                    content='Queued — Calls scheduled for today. Overdue — Calls scheduled for prior days and still not done.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Queued/Overdue info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{overDue + pending}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Queued:</span>
                                <span className={styles.noteValue}>{pending}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Overdue:</span>
                                <span className={styles.noteValue}>{overDue}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.inProgressCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                In Progress
                                <Tooltip content='Current calls with the AI agent.' maxWidth={350}>
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='In Progress info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{ringing + inProgress}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Ringing:</span>
                                <span className={styles.noteValue}>{ringing}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Active:</span>
                                <span className={styles.noteValue}>{inProgress}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.unableCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Unable to contact
                                <Tooltip
                                    content='Contact was unresponsive and the call was terminated. Bad numbers can be no USA numbers or disconnected lines. Blocked calls have been blocked by the phone carrier.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Unable to contact info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{failed + busy + noAnswer + badNumber + blocked}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{failed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Busy:</span>
                                <span className={styles.noteValue}>{busy}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Answer:</span>
                                <span className={styles.noteValue}>{noAnswer}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Bad Number:</span>
                                <span className={styles.noteValue}>{badNumber}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Blocked:</span>
                                <span className={styles.noteValue}>{blocked}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.voicemailCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Voicemail
                                <Tooltip
                                    content='Success - Voicemail was left. Failed - No voicemail could be left.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Voicemail info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{voicemail}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Success:</span>
                                <span className={styles.noteValue}>{voicemailSuccess}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{voicemailFailed}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.contactedCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Contacted
                                <Tooltip
                                    content='Contact was responsive. "User Abandoned" and "Agent Abandoned" indicate which party terminated the call. "Completed" reflects calls where the intended purpose was achieved'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Contacted info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{contacted}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{userAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Abandoned:</span>
                                <span className={styles.noteValue}>{agentAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Completed:</span>
                                <span className={styles.noteValue}>{completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.transferCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Transfer
                                <Tooltip
                                    content='Calls that are or were transferred to a Human. "No agents available", "Agent rejected" and "Transfer completed" are operational results from Human responses. "Transfer User abandoned" reflects contact drop.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Transfer info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{transfer}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Completed:</span>
                                <span className={styles.noteValue}>{transferCompleted}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{transferFailed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>In Progress:</span>
                                <span className={styles.noteValue}>{transferInProgress}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Agents:</span>
                                <span className={styles.noteValue}>{noAgentsAvailable}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Rejected:</span>
                                <span className={styles.noteValue}>{agentRejected}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{transferUserAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Human On Call:</span>
                                <span className={styles.noteValue}>{humanOnCall}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Accordion>
        </div>
    );
};
